(() => {
  const u_money = (cents, currency, locale) => {
    if (cents == null || Number.isNaN(Number(cents))) return "—";
    return new Intl.NumberFormat(locale || undefined, {
      style: "currency",
      currency: currency || "USD",
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(cents) / 100);
  };

  const u_packCountFromValue = (value) => {
    if (value == null) return 1;
    const m = String(value).match(/\d+/);
    return m ? parseInt(m[0], 10) : 1;
  };

  const u_sellingPlanIdStr = (id) => (id == null ? null : String(id));

  /*
   * Default subscription (“Deliver every”) by pack — edit here when plans change.
   * Pack number = digits in your first variant option label (example: "18-Pack" → 18).
   * Under each pack, add short text snippets that appear in the plan name in Shopify
   * (case does not matter). First matching plan in the store’s list wins; if none match,
   * the first plan in the dropdown is used.
   * To find exact names: Admin → product → subscription / selling plans, or view the
   * product page HTML and search for data-ds-pdp-config (plan_name inside JSON).
   * Optional: set window.DS_PDP_SUBSCRIPTION_DEFAULTS before this file to override
   * only the packs you pass (merged with the object below).
   */
  const DS_PDP_SUBSCRIPTION_DEFAULTS = {
    18: ["every 2 months", "every 2 month", "bi-month", "bi monthly", "bimonthly"],
    12: ["every 2 months", "every 2 month", "bi-month", "bi monthly", "bimonthly"],
    6: ["every 1 month", "delivery every 1 month", "monthly"],
  };

  const u_subscriptionDefaultsMerged = () => {
    const w =
      typeof window !== "undefined" ? window.DS_PDP_SUBSCRIPTION_DEFAULTS : null;
    if (!w || typeof w !== "object") return DS_PDP_SUBSCRIPTION_DEFAULTS;
    return { ...DS_PDP_SUBSCRIPTION_DEFAULTS, ...w };
  };

  const u_planNameMatchesAnyPhrase = (planName, phrases) => {
    const hay = String(planName || "").toLowerCase();
    return phrases.some((p) => hay.includes(String(p).toLowerCase()));
  };

  const u_defaultSellingPlanIdForPack = (allocations, packCount) => {
    if (!allocations?.length) return null;
    const fallback = u_sellingPlanIdStr(allocations[0].selling_plan_id);
    const phrases = u_subscriptionDefaultsMerged()[packCount];
    if (!phrases?.length) return fallback;
    const hit = allocations.find((a) =>
      u_planNameMatchesAnyPhrase(a.plan_name, phrases),
    );
    return hit ? u_sellingPlanIdStr(hit.selling_plan_id) : fallback;
  };

  const u_OnElementLoad = (selector, callback) => {
    let elapsed = 0;
    const interval = 100;
    const timeout = 10000;
    const timer = setInterval(() => {
      const element = document.querySelector(selector);
      elapsed += interval;
      if (element || elapsed >= timeout) {
        clearInterval(timer);
        callback(element);
      }
    }, interval);
  };

  if (typeof window.dsShopableVideoMuted !== "boolean") {
    window.dsShopableVideoMuted = true;
  }

  document.addEventListener("alpine:init", () => {
    Alpine.data("DSProduct", () => ({
      product: null,
      currentVariant: null,
      init() {
        this.product = JSON.parse(this.$el.dataset.product);
        this.currentVariant = JSON.parse(this.$el.dataset.currentVariant);
      },
    }));

    Alpine.data("DSPdpWidget", () => ({
      config: null,
      /** @type {Record<string, string | null>} */
      selected: { 1: null, 2: null, 3: null },
      purchaseType: "subscription",
      sellingPlanId: null,
      currentVariant: null,
      quantity: 1,
      /** @type {string} Theme `theme:product:{id}:variantChanged` id (section_product) */
      productId: "",

      init() {
        const script = this.$el.querySelector("[data-ds-pdp-config]");
        if (!script || !script.textContent.trim()) return;
        this.config = JSON.parse(script.textContent);
        this.productId = this.$el.dataset.dsPdpProductId || "";
        const initSel = this.config.initialSelected || {};
        this.selected = {
          1: initSel["1"] ?? null,
          2: initSel["2"] ?? null,
          3: initSel["3"] ?? null,
        };
        this.syncCurrentVariant();
        if (!this.hasSubscription) this.purchaseType = "one_time";
        this.syncSellingPlan();
        this.$watch(
          "selected",
          () => {
            this.syncCurrentVariant();
            this.syncSellingPlan();
            this.emitThemeVariant();
            this.syncThemeVariantInputs();
          },
          { deep: true },
        );
        this.$watch("purchaseType", () => this.syncSellingPlan());
        this.$watch(() => this.quantity, () => this.syncQuantityToForm());
        this.$nextTick(() => this.dsPdpAfterFirstPaint());

        // remove appstle widget
        u_OnElementLoad(".appstle_sub_widget", (element) => {
          element.remove();
        });
      },

      /** SV- form + subscribe UI after Alpine paints x-if dropdown */
      dsPdpAfterFirstPaint() {
        this.syncSellingPlan();
        this.syncQuantityToForm();
        this.emitThemeVariant();
        this.syncThemeVariantInputs();
        this.$nextTick(() => this.syncSellingPlan());
        requestAnimationFrame(() => this.syncSellingPlan());
      },

      themeVariantPayload() {
        const v = this.currentVariant;
        if (!v) return null;
        return {
          id: v.id,
          available: v.available,
          public_title: v.title || "",
          sku: v.sku || "",
          inventory_management: v.inventory_management || "",
          inventory_policy: v.inventory_policy || "",
          inventory_quantity: v.inventory_quantity ?? 0,
        };
      },

      emitThemeVariant() {
        if (!this.productId) return;
        const v = this.themeVariantPayload();
        if (!v) return;
        window.trigger(`theme:product:${this.productId}:variantChanged`, {
          detail: { variant: v },
        });
      },

      /**
       * Keeps theme `product-buy-buttons--input` (and fallback hidden `id`) in sync
       * with DS pack / option selection. Previously we skipped DOM updates whenever
       * buy-buttons existed, so variant_id never changed after pick.
       */
      syncThemeVariantInputs() {
        const form = this.$el.closest("form");
        if (!form) return;
        const v = this.currentVariant;
        const themeInputs = form.querySelectorAll(
          "input.product-buy-buttons--input[name='id']",
        );
        if (v && themeInputs.length) {
          themeInputs.forEach((inp) => {
            inp.value = String(v.id);
            inp.setAttribute("data-id", String(v.id));
            inp.setAttribute("data-sku", v.sku || "");
            inp.setAttribute("data-management", v.inventory_management || "");
            inp.setAttribute("data-policy", v.inventory_policy || "");
            inp.setAttribute("data-quantity", String(v.inventory_quantity ?? 0));
          });
          return;
        }
        if (!v) return;
        let inp = form.querySelector("input[data-ds-pdp-fallback-id]");
        if (!inp) {
          inp = document.createElement("input");
          inp.type = "hidden";
          inp.name = "id";
          inp.setAttribute("data-ds-pdp-fallback-id", "");
          form.appendChild(inp);
        }
        inp.value = String(v.id);
      },

      syncQuantityToForm() {
        const form = this.$el.closest("form");
        if (!form) return;
        const q = String(this.qtyLineMultiplier());
        form.querySelectorAll("[name=quantity]").forEach((el) => {
          if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
            el.value = q;
          }
        });
      },

      async handlePdpAddToCart() {
        const form = this.$el.closest("form");
        if (!form || !this.currentVariant?.available) return;
        this.syncQuantityToForm();
        this.emitThemeVariant();
        this.syncThemeVariantInputs();

        const buyRoot = form.querySelector("product-buy-buttons-element");
        if (buyRoot) {
          buyRoot.querySelectorAll(".product-buy-buttons--primary").forEach((btn) => {
            btn.setAttribute("aria-busy", "true");
          });
          if (typeof form.requestSubmit === "function") {
            form.requestSubmit();
          } else {
            form.dispatchEvent(
              new Event("submit", { bubbles: true, cancelable: true }),
            );
          }
          return;
        }

        await this.addToCartWithoutBuyButtons(form);
      },

      async addToCartWithoutBuyButtons(form) {
        const Cart = window.Cart;
        const submitBtn = this.$refs.pdpSubmit;
        submitBtn?.setAttribute?.("aria-busy", "true");
        if (typeof Cart === "undefined" || typeof Cart.addItems !== "function") {
          submitBtn?.removeAttribute?.("aria-busy");
          form.submit();
          return;
        }
        const fd = new FormData(form);
        const sections = Cart.sections ? Cart.sections.join(",") : "cart-drawer";
        const body = `${new URLSearchParams(fd).toString()}&sections=${sections}`;
        try {
          await Cart.addItems(
            {
              method: "POST",
              headers: { "Content-Type": "application/x-www-form-urlencoded" },
              body,
            },
            submitBtn || null,
          );
        } catch {
          /* theme feedback / errors */
        } finally {
          submitBtn?.removeAttribute?.("aria-busy");
        }
      },

      get isSubscription() {
        return this.purchaseType === "subscription" && this.hasSubscription;
      },

      get variants() {
        return this.config?.variants || [];
      },

      get options() {
        return this.config?.options || [];
      },

      syncCurrentVariant() {
        // SV- keep variant in sync for Alpine deps
        if (!this.config?.options?.length) {
          this.currentVariant = this.variants[0] || null;
          return;
        }
        const v =
          this.variants.find((variant) =>
            this.options.every(
              (opt) =>
                variant[`option${opt.position}`] ===
                this.selected[String(opt.position)],
            ),
          ) || null;
        this.currentVariant = v;
      },

      get hasSubscription() {
        const v = this.currentVariant;
        return Boolean(
          v?.selling_plan_allocations && v.selling_plan_allocations.length > 0,
        );
      },

      get currentAllocations() {
        return this.currentVariant?.selling_plan_allocations || [];
      },

      get activeAllocation() {
        if (this.purchaseType !== "subscription" || !this.sellingPlanId) {
          return null;
        }
        return (
          this.currentAllocations.find(
            (a) =>
              u_sellingPlanIdStr(a.selling_plan_id) ===
              u_sellingPlanIdStr(this.sellingPlanId),
          ) || this.currentAllocations[0] || null
        );
      },

      get caseChoices() {
        const first = this.options[0];
        if (!first) return [];
        const overlays = this.config?.caseChoiceImages || {};
        return first.values.map((value) => {
          const pick = this.pickVariantForOption(1, value);
          const override = overlays[value];
          const fromVariant = pick?.featured_image || null;
          return {
            value,
            image: override || fromVariant,
            available: pick ? pick.available : false,
          };
        });
      },

      pickVariantForOption(position, value) {
        const pos = Number(position);
        const candidates = this.variants.filter(
          (v) => v[`option${pos}`] === value,
        );
        if (!candidates.length) return null;
        const wanted = { ...this.selected, [String(pos)]: value };
        const exact = candidates.find((v) =>
          this.options.every(
            (opt) => v[`option${opt.position}`] === wanted[String(opt.position)],
          ),
        );
        if (exact) return exact;
        return candidates.find((v) => v.available) || candidates[0];
      },

      setOption(position, value) {
        const pick = this.pickVariantForOption(position, value);
        if (!pick) return;
        this.options.forEach((opt) => {
          this.selected[String(opt.position)] = pick[`option${opt.position}`];
        });
      },

      onOptionChange() {
        /* native select updates x-model */
      },

      syncSellingPlanDeliverySelectDom() {
        // SV- align native select after x-if/x-for paint
        if (
          this.purchaseType !== "subscription" ||
          !this.hasSubscription ||
          this.sellingPlanId == null
        ) {
          return;
        }
        const sel = this.$el?.querySelector?.(
          "select.ds-pdp-widget__select--delivery[name='selling_plan']",
        );
        if (!(sel instanceof HTMLSelectElement) || !sel.options?.length) return;
        const v = String(this.sellingPlanId);
        if (sel.value !== v) sel.value = v;
      },

      syncSellingPlan() {
        if (this.purchaseType === "subscription" && !this.hasSubscription) {
          this.purchaseType = "one_time";
        }
        if (this.purchaseType === "subscription" && this.hasSubscription) {
          const ids = new Set(
            this.currentAllocations.map((a) =>
              u_sellingPlanIdStr(a.selling_plan_id),
            ),
          );
          const cur = u_sellingPlanIdStr(this.sellingPlanId);
          if (cur === null || !ids.has(cur)) {
            this.sellingPlanId = u_defaultSellingPlanIdForPack(
              this.currentAllocations,
              this.packCount(),
            );
          }
        } else {
          this.sellingPlanId = null;
        }
        this.syncSellingPlanDeliverySelectDom();
      },

      formatMoney(cents) {
        return u_money(cents, this.config?.currency, this.config?.locale);
      },

      formatPlanLabel(allocation) {
        const n = allocation.plan_name;
        if (n && String(n).toLowerCase().includes("every")) return String(n).toUpperCase();
        return n ? String(n).toUpperCase() : "SUBSCRIPTION";
      },

      packCount() {
        const firstPos = this.options[0]?.position ?? 1;
        return u_packCountFromValue(this.selected[String(firstPos)]);
      },

      get oneTimePrice() {
        return this.currentVariant?.price ?? 0;
      },

      /** Unit quantity for line totals (integer ≥ 1). */
      qtyLineMultiplier() {
        const q = parseInt(this.quantity, 10);
        return Number.isFinite(q) && q >= 1 ? q : 1;
      },

      get oneTimePriceTotal() {
        return this.oneTimePrice * this.qtyLineMultiplier();
      },

      get subPriceTotal() {
        return this.subPrice * this.qtyLineMultiplier();
      },

      get oneTimePerBottle() {
        const n = this.packCount();
        const p = this.oneTimePrice;
        return n > 0 ? Math.round(p / n) : p;
      },

      get subPrice() {
        if (!this.hasSubscription) return this.oneTimePrice;
        const a = this.activeAllocation;
        return a?.price ?? this.currentAllocations[0]?.price ?? this.oneTimePrice;
      },

      get subCompareLine() {
        const a = this.activeAllocation || this.currentAllocations[0];
        if (!a) return { show: false, compare: null };
        const cmp = a.compare_at_price || this.currentVariant?.compare_at_price;
        return {
          show: Boolean(cmp && cmp > a.price),
          compare: cmp,
        };
      },

      get subPerBottle() {
        const n = this.packCount();
        const p = this.subPrice;
        return n > 0 ? Math.round(p / n) : p;
      },

      get savePercent() {
        const cmp = this.subCompareLine.compare;
        const price = this.subPrice;
        if (!cmp || cmp <= price) return null;
        return Math.round((1 - price / cmp) * 100);
      },

      get priceLine() {
        const v = this.currentVariant;
        if (!v) return { price: 0, compare_at: null, save: 0 };
        const useSub = this.purchaseType === "subscription" && this.hasSubscription;
        const price = useSub ? this.subPrice : v.price;
        const compare = useSub
          ? this.subCompareLine.compare || v.compare_at_price
          : v.compare_at_price;
        const save =
          compare && compare > price ? compare - price : 0;
        return { price, compare_at: compare, save };
      },
    }));

    Alpine.data("DSVideoCarousel", () => ({
      swiper: null,

      init() {
        this.$nextTick(() => this.mount());
      },

      destroy() {
        this.teardown();
      },

      teardown() {
        const root = this.$el;
        if (root._dsVideoControlsAbort) root._dsVideoControlsAbort.abort();
        const swEl = root.querySelector("[data-ds-video-swiper]");
        if (swEl?.swiper) swEl.swiper.destroy(true, true);
        this.swiper = null;
      },

      /**
       * Autoplay often fails if play() runs before the element can decode; retry after canplay.
       * @param {HTMLVideoElement} video
       */
      playActiveSlideVideo(video) {
        video.muted = window.dsShopableVideoMuted;
        const tryPlay = () => {
          video.play().catch(() => {});
        };
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          tryPlay();
        } else {
          video.addEventListener("canplay", tryPlay, { once: true });
          tryPlay();
        }
      },

      syncPlayback(activeIndex) {
        const root = this.$el;
        root.querySelectorAll("[data-slide-index]").forEach((slideEl) => {
          const i = parseInt(slideEl.dataset.slideIndex, 10);
          const isActive = i === activeIndex;

          const video = slideEl.querySelector("video.ds-shopable-video__video");
          if (!video) return;

          if (isActive) {
            this.playActiveSlideVideo(video);
          } else {
            video.pause();
            video.currentTime = 0;
          }
        });
      },

      updateUi(activeIndex) {
        const root = this.$el;
        root.querySelectorAll("[data-dot-index]").forEach((btn) => {
          const idx = parseInt(btn.getAttribute("data-dot-index") || "", 10);
          const on = idx === activeIndex;
          btn.classList.toggle("is-active", on);
          btn.setAttribute("aria-selected", on ? "true" : "false");
        });

        root.querySelectorAll("[data-ds-video-custom-ui]").forEach((el) => {
          const slide = el.closest("[data-slide-index]");
          const idx = slide ? parseInt(slide.dataset.slideIndex, 10) : -1;
          el.setAttribute("aria-hidden", idx === activeIndex ? "false" : "true");
        });
      },

      bindHtml5Controls() {
        const root = this.$el;
        if (root._dsVideoControlsAbort) root._dsVideoControlsAbort.abort();
        root._dsVideoControlsAbort = new AbortController();
        const { signal } = root._dsVideoControlsAbort;

        root.querySelectorAll(".ds-shopable-video__html5-wrap").forEach((wrap) => {
          const video = wrap.querySelector("video.ds-shopable-video__video");
          const progressEl = wrap.querySelector("[data-ds-video-progress]");
          const progressBar = wrap.querySelector("[data-ds-video-progress-bar]");
          const playBtn = wrap.querySelector("[data-ds-video-play-toggle]");
          const muteBtn = wrap.querySelector("[data-ds-video-mute-toggle]");
          const iconPlay = wrap.querySelector(".ds-shopable-video__ctrl-icon--play");
          const iconPause = wrap.querySelector(".ds-shopable-video__ctrl-icon--pause");
          const iconVolOn = wrap.querySelector(
            ".ds-shopable-video__ctrl-icon--volume-on",
          );
          const iconVolMuted = wrap.querySelector(
            ".ds-shopable-video__ctrl-icon--volume-muted",
          );
          if (!video) return;

          video.muted = window.dsShopableVideoMuted;

          let progressRafId = null;

          const updateProgress = () => {
            if (!video.duration || !Number.isFinite(video.duration)) return;
            const t = video.currentTime / video.duration;
            const pct = Math.min(100, Math.max(0, t * 100));
            if (progressBar) {
              progressBar.style.transform = `scaleX(${Math.min(1, Math.max(0, t))})`;
            }
            if (progressEl) {
              progressEl.setAttribute("aria-valuenow", String(Math.round(pct)));
            }
          };

          const progressTick = () => {
            if (signal.aborted) {
              progressRafId = null;
              return;
            }
            updateProgress();
            if (!video.paused && !video.ended) {
              progressRafId = requestAnimationFrame(progressTick);
            } else {
              progressRafId = null;
            }
          };

          const startProgressRaf = () => {
            if (signal.aborted) return;
            if (progressRafId != null) cancelAnimationFrame(progressRafId);
            progressRafId = requestAnimationFrame(progressTick);
          };

          const stopProgressRaf = () => {
            if (progressRafId != null) {
              cancelAnimationFrame(progressRafId);
              progressRafId = null;
            }
          };

          const syncPlayIcon = () => {
            const playing = !video.paused && !video.ended;
            if (iconPlay) iconPlay.hidden = playing;
            if (iconPause) iconPause.hidden = !playing;
            playBtn?.setAttribute("aria-label", playing ? "Pause" : "Play");
            playBtn?.setAttribute("aria-pressed", playing ? "true" : "false");
          };

          const syncMuteIcon = () => {
            const m = video.muted;
            if (iconVolOn) iconVolOn.hidden = m;
            if (iconVolMuted) iconVolMuted.hidden = !m;
            muteBtn?.setAttribute("aria-label", m ? "Unmute" : "Mute");
          };

          video.addEventListener("loadedmetadata", updateProgress, { signal });
          video.addEventListener("seeking", updateProgress, { signal });
          video.addEventListener("seeked", updateProgress, { signal });
          video.addEventListener(
            "ended",
            () => {
              stopProgressRaf();
              syncPlayIcon();
              updateProgress();
            },
            { signal },
          );
          video.addEventListener(
            "play",
            () => {
              syncPlayIcon();
              startProgressRaf();
            },
            { signal },
          );
          video.addEventListener(
            "pause",
            () => {
              stopProgressRaf();
              syncPlayIcon();
              updateProgress();
            },
            { signal },
          );
          video.addEventListener("volumechange", syncMuteIcon, { signal });

          playBtn?.addEventListener(
            "click",
            (e) => {
              e.stopPropagation();
              if (video.paused) video.play().catch(() => {});
              else video.pause();
            },
            { signal },
          );

          muteBtn?.addEventListener(
            "click",
            (e) => {
              e.stopPropagation();
              const nextMuted = !video.muted;
              window.dsShopableVideoMuted = nextMuted;
              root
                .querySelectorAll("video.ds-shopable-video__video")
                .forEach((v) => {
                  v.muted = nextMuted;
                });
            },
            { signal },
          );

          progressEl?.addEventListener(
            "click",
            (e) => {
              e.stopPropagation();
              if (!video.duration || !Number.isFinite(video.duration)) return;
              const rect = progressEl.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const ratio = Math.min(1, Math.max(0, x / rect.width));
              video.currentTime = ratio * video.duration;
            },
            { signal },
          );

          progressEl?.addEventListener(
            "keydown",
            (e) => {
              if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
              e.stopPropagation();
              if (!video.duration || !Number.isFinite(video.duration)) return;
              const delta = e.key === "ArrowLeft" ? -5 : 5;
              video.currentTime = Math.min(
                video.duration,
                Math.max(0, video.currentTime + delta),
              );
            },
            { signal },
          );

          syncPlayIcon();
          syncMuteIcon();
          updateProgress();
          if (!video.paused) startProgressRaf();
        });
      },

      mount() {
        this.teardown();
        const root = this.$el;
        const swiperEl = root.querySelector("[data-ds-video-swiper]");
        if (!swiperEl || typeof window.Swiper === "undefined") return;

        const slides = root.querySelectorAll(".swiper-slide[data-slide-index]");
        if (slides.length < 1) return;

        const prevBtn = root.querySelector("[data-ds-video-prev]");
        const nextBtn = root.querySelector("[data-ds-video-next]");
        const self = this;

        const swiper = new window.Swiper(swiperEl, {
          centeredSlides: true,
          centeredSlidesBounds: true,
          slidesPerView: 1.5,
          spaceBetween: 12,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          loop: true,
          breakpoints: {
            990: {
              slidesPerView: 5,
              spaceBetween: 18,
            },
          },
          navigation: {
            prevEl: prevBtn,
            nextEl: nextBtn,
          },
          on: {
            init(sw) {
              const idx = sw.realIndex;
              self.syncPlayback(idx);
              self.updateUi(idx);
            },
            slideChange(sw) {
              const idx = sw.realIndex;
              self.syncPlayback(idx);
              self.updateUi(idx);
            },
          },
        });

        this.swiper = swiper;

        root.querySelectorAll("[data-dot-index]").forEach((btn) => {
          btn.onclick = () => {
            const idx = parseInt(btn.getAttribute("data-dot-index") || "", 10);
            if (Number.isFinite(idx)) swiper.slideTo(idx);
          };
        });

        this.bindHtml5Controls();

        queueMicrotask(() => {
          if (!root.isConnected) return;
          const s = root.querySelector("[data-ds-video-swiper]")?.swiper;
          if (s) {
            self.syncPlayback(s.realIndex);
            self.updateUi(s.realIndex);
          }
        });
      },
    }));
  });

  const scrollActiveThumbIntoStripViewport = (strip, mediaId) => {
    const viewport = strip?.querySelector?.(".product-media--thumbs-viewport");
    const activeThumb = strip?.querySelector?.(
      `.product-media--thumb[data-id="${mediaId}"]`,
    );
    if (!viewport || !activeThumb) return;
    const maxLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const targetLeft = Math.min(
      Math.max(
        0,
        activeThumb.offsetLeft -
          (viewport.clientWidth - activeThumb.offsetWidth) / 2,
      ),
      maxLeft,
    );
    viewport.scrollTo({ left: targetLeft, behavior: "smooth" });
  };

  const initProductThumbsStripNav = () => {
    document.querySelectorAll(".product-media--thumbs-shell").forEach((shell) => {
      if (shell.dataset.stripNavInit === "true") return;
      shell.dataset.stripNavInit = "true";
      const viewport = shell.querySelector(".product-media--thumbs-viewport");
      const prev = shell.querySelector(".product-media--thumbs-nav--prev");
      const next = shell.querySelector(".product-media--thumbs-nav--next");
      if (!viewport || !prev || !next) return;

      const pageDelta = () => viewport.clientWidth;

      const updateStripNav = () => {
        const max = viewport.scrollWidth - viewport.clientWidth;
        if (max <= 2) {
          prev.disabled = true;
          next.disabled = true;
          return;
        }
        prev.disabled = viewport.scrollLeft <= 2;
        next.disabled = viewport.scrollLeft >= max - 2;
      };

      prev.addEventListener("click", (e) => {
        e.preventDefault();
        viewport.scrollBy({
          left: -pageDelta(),
          behavior: "smooth",
        });
      });
      next.addEventListener("click", (e) => {
        e.preventDefault();
        viewport.scrollBy({
          left: pageDelta(),
          behavior: "smooth",
        });
      });
      viewport.addEventListener("scroll", updateStripNav, { passive: true });
      new ResizeObserver(updateStripNav).observe(viewport);
      updateStripNav();
    });
  };

  const initProductMediaDesktopNav = () => {
    document
      .querySelectorAll(
        'product-media-variants.product-media--variants--root[data-layout="thumbnails"] .product-media--main-shell',
      )
      .forEach((shell) => {
        if (shell.dataset.navInit === "true") return;
        shell.dataset.navInit = "true";
        const root = shell.closest("product-media-variants");
        if (!root) return;
        const productId = root.dataset.id;
        const prev = shell.querySelector(".product-media--nav--prev");
        const next = shell.querySelector(".product-media--nav--next");
        if (!prev || !next || !productId) return;

        const getMedia = () => [
          ...root.querySelectorAll(".product-media--container .product-media--root"),
        ];
        const getIndex = () => {
          const media = getMedia();
          return media.findIndex((el) => el.getAttribute("data-active") === "true");
        };

        const syncThumbs = (mediaId) => {
          const strip = document.querySelector(
            `product-thumbs-element[data-id="${productId}"]`,
          );
          if (!strip) return;
          strip.querySelectorAll(".product-media--thumb").forEach((t) => {
            t.setAttribute(
              "data-active",
              parseInt(t.dataset.id, 10) === mediaId,
            );
          });
          scrollActiveThumbIntoStripViewport(strip, mediaId);
        };

        const updateNavState = () => {
          const media = getMedia();
          const n = media.length;
          let idx = getIndex();
          if (idx === -1) idx = 0;
          if (n < 2) {
            prev.disabled = true;
            next.disabled = true;
            return;
          }
          prev.disabled = idx <= 0;
          next.disabled = idx >= n - 1;
        };

        const go = (delta) => {
          const media = getMedia();
          if (media.length < 2) return;
          let idx = getIndex();
          if (idx === -1) idx = 0;
          const nextIdx = idx + delta;
          if (nextIdx < 0 || nextIdx >= media.length) return;
          const id = parseInt(media[nextIdx].dataset.id, 10);
          window.trigger(`theme:product:${productId}:mediaChanged`, { id });
          syncThumbs(id);
          updateNavState();
        };

        prev.addEventListener("click", (e) => {
          e.preventDefault();
          go(-1);
        });
        next.addEventListener("click", (e) => {
          e.preventDefault();
          go(1);
        });

        const onMediaOrVariant = () => {
          updateNavState();
          requestAnimationFrame(() => {
            const media = getMedia();
            let idx = getIndex();
            if (idx === -1) idx = 0;
            const m = media[idx];
            if (!m) return;
            const strip = document.querySelector(
              `product-thumbs-element[data-id="${productId}"]`,
            );
            scrollActiveThumbIntoStripViewport(
              strip,
              parseInt(m.dataset.id, 10),
            );
          });
        };
        window.addEventListener(
          `theme:product:${productId}:mediaChanged`,
          onMediaOrVariant,
        );
        window.addEventListener(
          `theme:product:${productId}:variantChanged`,
          onMediaOrVariant,
        );
        updateNavState();
      });
  };

  document.addEventListener("DOMContentLoaded", () => {
    initProductThumbsStripNav();
    initProductMediaDesktopNav();
  });
  document.addEventListener("shopify:section:load", () => {
    initProductThumbsStripNav();
    initProductMediaDesktopNav();
  });
})();
